<template>
  <div ref="containerDiv" class="canvasBox">
    <canvas ref="myCanvas" :width="canvasSize" :height="canvasSize"
      style="touch-action:none;"
    ></canvas>

    <!-- 定时器控制按钮 -->
    <div style="margin-top: 20px; text-align: center; font-family: Arial, sans-serif;">
      <div style="background: #e8f4f8; padding: 15px; border-radius: 8px; display: inline-block; margin-bottom: 15px;">
        <h3 style="margin: 0 0 10px 0; color: #333;">定时器控制</h3>
        <div style="display: flex; gap: 15px; align-items: center; justify-content: center; flex-wrap: wrap;">
          <button
            @click="startTimer"
            :disabled="isTimerRunning"
            style="padding: 8px 16px; background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
            :style="{ opacity: isTimerRunning ? 0.5 : 1 }"
          >
            开始定时器
          </button>
          <button
            @click="stopTimer"
            :disabled="!isTimerRunning"
            style="padding: 8px 16px; background: #f44336; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
            :style="{ opacity: !isTimerRunning ? 0.5 : 1 }"
          >
            停止定时器
          </button>
          <button
            @click="resetTimer"
            style="padding: 8px 16px; background: #ff9800; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
          >
            重置
          </button>
          <div style="display: flex; align-items: center; gap: 10px; margin-left: 20px;">
            <label for="timeIndexInput" style="font-weight: bold;">跳转到索引:</label>
            <input
              id="timeIndexInput"
              v-model.number="jumpToIndex"
              type="number"
              :min="0"
              :max="dataSegments - 1"
              style="width: 80px; padding: 4px; border: 1px solid #ccc; border-radius: 4px;"
            />
            <button
              @click="jumpToTimeIndex"
              :disabled="jumpToIndex < 0 || jumpToIndex >= dataSegments"
              style="padding: 6px 12px; background: #2196F3; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;"
              :style="{ opacity: (jumpToIndex < 0 || jumpToIndex >= dataSegments) ? 0.5 : 1 }"
            >
              跳转
            </button>
          </div>
          <div style="margin-left: 20px;">
            <strong>当前时间索引:</strong> {{ currentTimeIndex + 1 }} / {{ dataSegments }}
          </div>
          <div>
            <strong>定时器状态:</strong>
            <span :style="{ color: isTimerRunning ? '#4CAF50' : '#f44336', fontWeight: 'bold' }">
              {{ isTimerRunning ? '运行中' : '已停止' }}
            </span>
          </div>
          <div v-if="currentTimeData" style="margin-left: 20px;">
            <strong>当前时间:</strong> {{ currentTimeData.receivedTime }}
          </div>
          <div v-if="currentTimeData">
            <strong>当前状态:</strong>
            <span :style="{
              color: getStatusColor(currentTimeData.attitude),
              fontWeight: 'bold'
            }">
              {{ getStatusText(currentTimeData.attitude) }}
            </span>
          </div>
          <div v-if="timeIntervals.length > 0 && currentTimeIndex < timeIntervals.length">
            <strong>下次间隔:</strong> {{ (timeIntervals[currentTimeIndex] || 1).toFixed(1) }}秒
          </div>
        </div>
      </div>
    </div>

    <!-- 显示当前指针位置的数据值 -->
    <div style="margin-top: 20px; text-align: center; font-family: Arial, sans-serif;">
      <div v-if="currentPointerValue" style="background: #f0f0f0; padding: 15px; border-radius: 8px; display: inline-block;">
        <h3 style="margin: 0 0 10px 0; color: #333;">当前指针位置数据（与定时器同步）</h3>
        <div style="display: flex; gap: 20px; align-items: center; flex-wrap: wrap;">
          <div>
            <strong>数据段:</strong> {{ currentPointerValue.index }} / {{ dataSegments }}
          </div>
          <div>
            <strong>状态:</strong>
            <span :style="{
              color: currentPointerValue.colorType === 'red' ? '#ff0000' :
                     currentPointerValue.colorType === 'yellow' ? '#ffaa00' :
                     currentPointerValue.colorType === 'green' ? '#00ff00' :
                     currentPointerValue.colorType === 'purple' ? '#800080' :
                     currentPointerValue.colorType === 'blue' ? '#0000ff' : '#808080',
              fontWeight: 'bold'
            }">
              {{ getStatusText(currentPointerValue.status) }}
            </span>
          </div>
          <div>
            <strong>时间:</strong> {{ currentPointerValue.receivedTime }}
          </div>
          <div>
            <strong>角度:</strong> {{ currentPointerValue.angle }}
          </div>
        </div>
      </div>
      <div v-else-if="list && list.length > 0" style="background: #f0f0f0; padding: 15px; border-radius: 8px; display: inline-block; color: #666;">
        数据加载中...
      </div>
      <div v-else style="background: #f0f0f0; padding: 15px; border-radius: 8px; display: inline-block; color: #666;">
        暂无数据
      </div>
    </div>
  </div>
</template>

<script>
import obj from './obj.json'
export default {
  props: {
    // 容器宽度，可以从外部传入
    width: {
      type: Number,
      default: 350
    }
  },
  data() {
    return {
      // 响应式尺寸属性
      containerWidth: this.width, // 容器宽度，从props获取

      // 动态计算的尺寸属性（将在computed中计算）
      center: { x: 195, y: 195 },
      radius: 150,

      startAngle: Math.PI * 1.5,
      arcLen: Math.PI / 3,
      dragging: false,
      dragOffset: 0,
      pointerAngle: -Math.PI / 2,
      snapRange: 0.05,
      dragStartAngle: null,
      baseStart: Math.PI * 1.1,
      baseEnd: Math.PI * 1.9,
      // 黄色弧线属性
      yellowArcLen: Math.PI / 4, // 黄色弧线长度（45度）

      // 红色数据弧线属性
      dataArcLength: Math.PI * 1.5, // 数据弧线总长度（270度）
      dataSegments: 0, // 数据段数，将根据list长度动态设置
      dataStartAngle: -Math.PI / 2, // 初始位置在黑色指针位置（正上方）
      dataValues: [], // 存储数据值
      isDraggingDataArc: false,
      dataArcDragOffset: 0,
      list: obj,

      // 定时器相关属性
      timer: null, // 定时器实例
      isTimerRunning: false, // 定时器运行状态
      currentTimeIndex: 0, // 当前时间索引
      timeIntervals: [], // 时间间隔数组
      autoSlideSpeed: 1000, // 自动滑动速度（毫秒）
      totalTimeSpan: 0, // 总时间跨度（秒）
      jumpToIndex: 0 // 手动跳转的目标索引
    }
  },
  computed: {
    // 响应式尺寸计算
    canvasSize() {
      return this.containerWidth * 1.11; // canvas比容器稍大一点，留出边距
    },
    scaleFactor() {
      return this.containerWidth / 350; // 基于350px的缩放比例
    },
    responsiveCenter() {
      const size = this.canvasSize;
      return { x: size / 2, y: size / 2 };
    },
    responsiveRadius() {
      return 150 * this.scaleFactor;
    },
    // 响应式线宽
    baseArcLineWidth() {
      return Math.max(20, 40 * this.scaleFactor); // 底色弧线宽度，最小20px
    },
    dataArcLineWidth() {
      return Math.max(5, 10 * this.scaleFactor); // 数据弧线宽度，最小5px
    },
    pointerLineWidth() {
      return Math.max(2, 4 * this.scaleFactor); // 指针线宽，最小2px
    },
    scaleLineWidth() {
      return Math.max(1, 2 * this.scaleFactor); // 刻度线宽，最小1px
    },
    pointerLength() {
      return 30 * this.scaleFactor; // 指针长度
    },

    endAngle() {
      return this.startAngle + this.arcLen;
    },
    // 黄色弧线的开始位置（紧接在红色弧线后面）
    yellowStartAngle() {
      return this.endAngle;
    },
    // 黄色弧线的结束位置
    yellowEndAngle() {
      return this.yellowStartAngle + this.yellowArcLen;
    },
    // 数据弧线的结束角度
    dataEndAngle() {
      return this.dataStartAngle + this.dataArcLength;
    },
    // 每个数据段的角度大小
    segmentAngle() {
      return this.dataArcLength / this.dataSegments;
    },
    // 获取当前时间索引对应的数据
    currentTimeData() {
      if (this.currentTimeIndex >= 0 && this.currentTimeIndex < this.list.length) {
        return this.list[this.currentTimeIndex];
      }
      return null;
    },
    // 计算黑色指针位置对应的数据值 - 与定时器控制同步
    currentPointerValue() {
      // 使用与定时器控制相同的数据源，确保信息同步
      if (this.currentTimeData) {
        // 计算当前时间索引对应的数据段索引
        const segmentIndex = this.currentTimeIndex;

        // 状态到颜色的映射
        const statusColorMap = {
          'running': 'red',      // 跑 - 红色
          'sitting': 'yellow',   // 坐 - 黄色
          'standing': 'green',   // 站立 - 绿色
          'walking': 'purple',   // 行走 - 紫色
          'lying': 'blue',       // 爬 - 蓝色
          'eatting': 'gray'      // 吃 - 灰色
        };

        return {
          index: segmentIndex + 1, // 显示从1开始的索引
          value: '50.00', // 固定值，与dataValues中的值保持一致
          colorType: statusColorMap[this.currentTimeData.attitude] || 'red',
          status: this.currentTimeData.attitude,
          receivedTime: this.currentTimeData.receivedTime,
          angle: (this.pointerAngle * 180 / Math.PI).toFixed(1) + '°'
        };
      } else if (this.list && this.list.length > 0 && this.dataValues.length > 0) {
        // 如果currentTimeData为空但有数据，使用第一个数据项
        const firstItem = this.list[0];
        const firstDataValue = this.dataValues[0];

        return {
          index: 1, // 显示从1开始的索引
          value: '50.00', // 固定值
          colorType: firstDataValue.colorType,
          status: firstItem.attitude,
          receivedTime: firstItem.receivedTime,
          angle: (this.pointerAngle * 180 / Math.PI).toFixed(1) + '°'
        };
      }

      return null;
    }
  },
  watch: {
    // 监听width props的变化
    width(newWidth) {
      this.containerWidth = newWidth;
      this.initializeResponsiveSizes();
      this.drawArc();
    }
  },
  mounted() {
    console.log('=== 组件挂载开始 ===');
    console.log('list数据:', this.list);
    console.log('list长度:', this.list ? this.list.length : 'undefined');
    console.log('list前5项:', this.list ? this.list.slice(0, 5) : 'undefined');

    // 初始化响应式尺寸
    this.initializeResponsiveSizes();
    this.initializeDataValues();
    this.initializeTimer();
    this.drawArc();
    const canvas = this.$refs.myCanvas;
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onTouchEnd, { passive: false });

    // 添加鼠标事件支持（用于桌面端测试）
    canvas.addEventListener('mousedown', this.onMouseDown, { passive: false });
    canvas.addEventListener('mousemove', this.onMouseMove, { passive: false });
    canvas.addEventListener('mouseup', this.onMouseUp, { passive: false });
    canvas.addEventListener('mouseleave', this.onMouseUp, { passive: false });

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // 清理定时器
    this.stopTimer();

    const canvas = this.$refs.myCanvas;
    canvas.removeEventListener('touchstart', this.onTouchStart);
    canvas.removeEventListener('touchmove', this.onTouchMove);
    canvas.removeEventListener('touchend', this.onTouchEnd);
    canvas.removeEventListener('mousedown', this.onMouseDown);
    canvas.removeEventListener('mousemove', this.onMouseMove);
    canvas.removeEventListener('mouseup', this.onMouseUp);
    canvas.removeEventListener('mouseleave', this.onMouseUp);

    // 清理窗口事件监听
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // 初始化响应式尺寸
    initializeResponsiveSizes() {
      // 更新中心点和半径
      this.center = this.responsiveCenter;
      this.radius = this.responsiveRadius;
      console.log('初始化响应式尺寸:', {
        containerWidth: this.containerWidth,
        canvasSize: this.canvasSize,
        scaleFactor: this.scaleFactor,
        center: this.center,
        radius: this.radius
      });
    },

    // 处理窗口大小变化
    handleResize() {
      // 可以根据需要调整容器宽度
      // 这里保持固定宽度，如果需要响应式可以修改
      this.initializeResponsiveSizes();
      this.drawArc();
    },

    // 公共方法：动态设置容器宽度
    setContainerWidth(width) {
      this.containerWidth = width;
      this.initializeResponsiveSizes();
      this.drawArc();
    },

    // 公共方法：获取当前尺寸信息
    getSizeInfo() {
      return {
        containerWidth: this.containerWidth,
        canvasSize: this.canvasSize,
        scaleFactor: this.scaleFactor,
        center: this.center,
        radius: this.radius,
        lineWidths: {
          baseArc: this.baseArcLineWidth,
          dataArc: this.dataArcLineWidth,
          pointer: this.pointerLineWidth,
          scale: this.scaleLineWidth
        }
      };
    },

    // 初始化定时器相关数据
    initializeTimer() {
      console.log('初始化定时器数据');

      if (!this.list || this.list.length === 0) {
        console.error('无法初始化定时器：list数据为空');
        return;
      }

      // 解析时间并计算时间间隔
      this.timeIntervals = [];
      let previousTime = null;

      // 由于数据是按时间倒序排列的，我们需要从后往前处理
      for (let i = this.list.length - 1; i >= 0; i--) {
        const currentTime = new Date(this.list[i].receivedTime);

        if (previousTime) {
          // 计算与前一个时间的间隔（秒）
          const intervalSeconds = Math.abs((currentTime - previousTime) / 1000);
          // 如果间隔太小（小于0.5秒）或太大（大于60秒），使用默认值1秒
          const normalizedInterval = (intervalSeconds < 0.5 || intervalSeconds > 60) ? 1 : intervalSeconds;
          this.timeIntervals.push(normalizedInterval);
        } else {
          // 第一个数据点，默认间隔1秒
          this.timeIntervals.push(1);
        }

        previousTime = currentTime;
      }

      // 反转时间间隔数组，使其与数据顺序一致
      this.timeIntervals.reverse();

      // 计算总时间跨度
      if (this.list.length > 1) {
        const firstTime = new Date(this.list[this.list.length - 1].receivedTime);
        const lastTime = new Date(this.list[0].receivedTime);
        this.totalTimeSpan = Math.abs((lastTime - firstTime) / 1000);
      }

      console.log('时间间隔数组前10项:', this.timeIntervals.slice(0, 10));
      console.log('总时间跨度:', this.totalTimeSpan, '秒');
      console.log('数据时间范围:', this.list[this.list.length - 1].receivedTime, '到', this.list[0].receivedTime);
    },

    // 启动定时器
    startTimer() {
      if (this.isTimerRunning) {
        console.log('定时器已在运行');
        return;
      }

      console.log('启动定时器');
      this.isTimerRunning = true;
      this.runTimerStep();
    },

    // 停止定时器
    stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      this.isTimerRunning = false;
      console.log('定时器已停止');
    },

    // 重置定时器
    resetTimer() {
      this.stopTimer();
      this.currentTimeIndex = 0;
      this.jumpToIndex = 0;
      // 重置数据弧线到初始位置（时间索引0对应的位置）
      this.slideDataArcToTimeIndex(0);
      console.log('定时器已重置到初始位置');
    },

    // 手动跳转到指定时间索引
    jumpToTimeIndex() {
      if (this.jumpToIndex < 0 || this.jumpToIndex >= this.dataSegments) {
        console.warn('跳转索引超出范围:', this.jumpToIndex);
        return;
      }

      // 停止当前定时器
      this.stopTimer();

      // 设置当前时间索引
      this.currentTimeIndex = this.jumpToIndex;

      // 滑动数据弧线到指定位置
      this.slideDataArcToTimeIndex(this.jumpToIndex);

      console.log(`手动跳转到时间索引: ${this.jumpToIndex + 1}/${this.dataSegments}`);
    },

    // 执行定时器步骤
    runTimerStep() {
      if (!this.isTimerRunning || this.currentTimeIndex >= this.dataSegments) {
        this.stopTimer();
        console.log('定时器执行完毕');
        return;
      }

      // 计算当前时间间隔（使用实际时间间隔或默认1秒）
      const currentInterval = this.timeIntervals[this.currentTimeIndex] || 1;
      // 将时间间隔限制在合理范围内：最小0.5秒，最大3秒
      const intervalMs = Math.max(500, Math.min(currentInterval * 1000, 3000));

      console.log(`定时器步骤 ${this.currentTimeIndex + 1}/${this.dataSegments}, 间隔: ${(intervalMs/1000).toFixed(1)}秒`);

      // 滑动数据弧线到当前时间索引对应的位置
      this.slideDataArcToTimeIndex(this.currentTimeIndex);

      // 移动到下一个时间索引
      this.currentTimeIndex++;

      // 设置下一次执行
      this.timer = setTimeout(() => {
        this.runTimerStep();
      }, intervalMs);
    },

    // 滑动数据弧线到指定的时间索引位置
    slideDataArcToTimeIndex(timeIndex) {
      // 计算每个数据段的角度
      const segmentAngle = this.segmentAngle;

      // 计算目标位置：从初始位置（-π/2）开始，向逆时针方向移动 timeIndex 个段的角度
      const initialStartAngle = -Math.PI / 2; // 初始位置在黑色指针位置（正上方）
      const targetStartAngle = initialStartAngle - (timeIndex * segmentAngle);

      // 标准化角度到 [-π, π] 范围
      const normalizeToRange = (angle) => {
        while (angle > Math.PI) angle -= 2 * Math.PI;
        while (angle <= -Math.PI) angle += 2 * Math.PI;
        return angle;
      };

      let normalizedTargetAngle = normalizeToRange(targetStartAngle);

      // 检查滑动限制
      const pointerPosition = this.pointerAngle; // -Math.PI / 2
      const minDataStart = pointerPosition - this.dataArcLength;
      const maxDataStart = pointerPosition;
      const normalizedMin = normalizeToRange(minDataStart);
      const normalizedMax = normalizeToRange(maxDataStart);

      // 应用滑动限制
      if (normalizedMin <= normalizedMax) {
        // 正常情况，不跨越-π/π边界
        if (normalizedTargetAngle < normalizedMin) {
          normalizedTargetAngle = normalizedMin;
          console.log('达到左滑动限制，停止在限制位置');
        }
        if (normalizedTargetAngle > normalizedMax) {
          normalizedTargetAngle = normalizedMax;
          console.log('达到右滑动限制，停止在限制位置');
        }
      } else {
        // 跨越-π/π边界的情况
        if (normalizedTargetAngle > normalizedMax && normalizedTargetAngle < normalizedMin) {
          // 在禁止区域内，选择最近的边界
          const distToMax = Math.abs(normalizedTargetAngle - normalizedMax);
          const distToMin = Math.abs(normalizedTargetAngle - normalizedMin);
          if (distToMax < distToMin) {
            normalizedTargetAngle = normalizedMax;
            console.log('达到右滑动限制（跨边界）');
          } else {
            normalizedTargetAngle = normalizedMin;
            console.log('达到左滑动限制（跨边界）');
          }
        }
      }

      // 设置数据弧线的新起始位置
      this.dataStartAngle = normalizedTargetAngle;

      // 获取当前时间信息用于日志
      const currentTimeData = this.list[timeIndex];
      const currentTime = currentTimeData ? currentTimeData.receivedTime : '未知';

      console.log(`数据弧线滑动到时间索引 ${timeIndex + 1}/${this.dataSegments}`);
      console.log(`对应时间: ${currentTime}`);
      console.log(`弧线起始角度: ${(normalizedTargetAngle * 180 / Math.PI).toFixed(1)}°`);

      // 重新绘制
      this.drawArc();
    },

    // 根据数据弧线位置更新当前时间索引，实现数据同步
    updateCurrentTimeIndexFromDataArc() {
      // 黑色指针的角度（正上方，-π/2）
      const pointerAngle = this.pointerAngle;

      // 计算指针相对于数据弧线起始位置的角度差
      let angleDiff = pointerAngle - this.dataStartAngle;

      // 标准化角度差到 [0, 2π] 范围
      while (angleDiff < 0) angleDiff += Math.PI * 2;
      while (angleDiff >= Math.PI * 2) angleDiff -= Math.PI * 2;

      // 如果指针不在数据弧线范围内，不更新时间索引
      if (angleDiff > this.dataArcLength) {
        return;
      }

      // 计算对应的数据段索引
      const segmentIndex = Math.floor(angleDiff / this.segmentAngle);

      // 确保索引在有效范围内，并更新当前时间索引
      if (segmentIndex >= 0 && segmentIndex < this.dataSegments) {
        this.currentTimeIndex = segmentIndex;
        this.jumpToIndex = segmentIndex; // 同步更新跳转输入框的值

        console.log(`数据同步：当前时间索引更新为 ${segmentIndex + 1}/${this.dataSegments}`);
        console.log(`对应时间: ${this.list[segmentIndex] ? this.list[segmentIndex].receivedTime : '未知'}`);
      }
    },

    // 初始化数据值
    initializeDataValues() {
      console.log('开始初始化数据，list长度:', this.list ? this.list.length : 'list为空');

      if (!this.list || this.list.length === 0) {
        console.error('list数据为空或未定义');
        return;
      }

      // 使用list中的数据
      this.dataValues = [];
      this.dataSegments = this.list.length; // 根据实际数据长度设置段数

      console.log('设置数据段数为:', this.dataSegments);

      // 状态到颜色的映射
      const statusColorMap = {
        'running': 'red',      // 跑 - 红色
        'sitting': 'yellow',   // 坐 - 黄色
        'standing': 'green',   // 站立 - 绿色
        'walking': 'purple',   // 行走 - 紫色
        'lying': 'blue',       // 爬 - 蓝色
        'eatting': 'gray'      // 吃 - 灰色
      };

      for (let i = 0; i < this.list.length; i++) {
        const item = this.list[i];
        const attitude = item.attitude || 'running'; // 使用attitude字段作为状态
        const colorType = statusColorMap[attitude] || 'red'; // 默认红色

        this.dataValues.push({
          value: 50, // 固定值，用于透明度计算
          colorType: colorType,
          status: attitude,
          receivedTime: item.receivedTime || '', // 保存接收时间
          originalData: item // 保存原始数据
        });
      }

      console.log('初始化数据完成，总段数:', this.dataSegments);
      console.log('数据样例:', this.dataValues.slice(0, 5));
      console.log('segmentAngle:', this.segmentAngle);
    },

    getTouchPos(e) {
      const rect = this.$refs.myCanvas.getBoundingClientRect();
      const touch = e.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    },
    normalizeAngle(angle) {
      while (angle < 0) angle += Math.PI * 2;
      while (angle >= Math.PI * 2) angle -= Math.PI * 2;
      return angle;
    },
    angleFromCenter(pos) {
      return this.normalizeAngle(Math.atan2(pos.y - this.center.y, pos.x - this.center.x));
    },
    onTouchStart(e) {
      e.preventDefault();
      const pos = this.getTouchPos(e);
      const angle = this.angleFromCenter(pos);

      // 优先检查是否点击在数据弧线附近
      if (this.isNearDataArc(angle)) {
        this.isDraggingDataArc = true;
        this.dataArcDragOffset = angle - this.dataStartAngle;
        console.log('触摸开始拖拽数据弧线');
      }
      // 检查是否点击在红色圆弧附近
      else if (this.isNearRedArc(angle)) {
        this.dragging = true;
        this.dragOffset = angle - this.startAngle;
        this.dragStartAngle = this.startAngle;
        console.log('触摸开始拖拽红色圆弧');
      } else {
        this.dragging = false;
        this.isDraggingDataArc = false;
      }
    },
    onTouchMove(e) {
      if (!this.dragging && !this.isDraggingDataArc) return;
      e.preventDefault();
      const pos = this.getTouchPos(e);
      let angle = this.angleFromCenter(pos);

      if (this.isDraggingDataArc) {
        // 处理数据弧线拖拽
        let newDataStart = angle - this.dataArcDragOffset;

        // 黑色指针的位置
        const pointerPosition = this.pointerAngle; // -Math.PI / 2

        // 计算滑动限制：
        // 1. 向右滑动限制：数据弧线起始位置不能超过指针位置
        const maxDataStart = pointerPosition;

        // 2. 向左滑动限制：数据弧线末尾位置不能超过指针位置
        // 当数据弧线末尾与指针重叠时：newDataStart + dataArcLength = pointerPosition
        // 所以：newDataStart = pointerPosition - dataArcLength
        const minDataStart = pointerPosition - this.dataArcLength;

        // 标准化角度到 [-π, π] 范围，避免跳转问题
        const normalizeToRange = (angle) => {
          while (angle > Math.PI) angle -= 2 * Math.PI;
          while (angle <= -Math.PI) angle += 2 * Math.PI;
          return angle;
        };

        newDataStart = normalizeToRange(newDataStart);
        const normalizedMin = normalizeToRange(minDataStart);
        const normalizedMax = normalizeToRange(maxDataStart);

        // 应用滑动限制，防止跳转
        if (normalizedMin <= normalizedMax) {
          // 正常情况，不跨越-π/π边界
          if (newDataStart < normalizedMin) {
            newDataStart = normalizedMin;
            console.log('达到左滑动限制：数据弧线末尾与指针重叠');
          }
          if (newDataStart > normalizedMax) {
            newDataStart = normalizedMax;
            console.log('达到右滑动限制：数据弧线起始位置与指针重叠');
          }
        } else {
          // 跨越-π/π边界的情况
          if (newDataStart > normalizedMax && newDataStart < normalizedMin) {
            // 在禁止区域内，选择最近的边界
            const distToMax = Math.abs(newDataStart - normalizedMax);
            const distToMin = Math.abs(newDataStart - normalizedMin);
            if (distToMax < distToMin) {
              newDataStart = normalizedMax;
              console.log('达到右滑动限制（跨边界）');
            } else {
              newDataStart = normalizedMin;
              console.log('达到左滑动限制（跨边界）');
            }
          }
        }

        this.dataStartAngle = newDataStart;

        // 计算当前指针位置对应的时间索引，实现数据同步
        this.updateCurrentTimeIndexFromDataArc();

        // 计算数据弧线的结束位置用于调试
        const dataEndAngle = normalizeToRange(newDataStart + this.dataArcLength);
        console.log('数据弧线起始位置:', (newDataStart * 180 / Math.PI).toFixed(1) + '°');
        console.log('数据弧线结束位置:', (dataEndAngle * 180 / Math.PI).toFixed(1) + '°');
        console.log('指针位置:', (pointerPosition * 180 / Math.PI).toFixed(1) + '°');
      } else if (this.dragging) {
        // 处理原有红色弧线拖拽
        let newStart = angle - this.dragOffset;
        const pointerAngle = this.normalizeAngle(this.pointerAngle + 2 * Math.PI);

        if (newStart > pointerAngle) {
          newStart = pointerAngle;
          console.log('红色弧线开始位置不能超过指针，限制右滑');
        }

        this.startAngle = newStart;
        console.log('红色弧线位置:', {
          start: (newStart * 180 / Math.PI).toFixed(1) + '°',
          end: ((newStart + this.arcLen) * 180 / Math.PI).toFixed(1) + '°',
          pointer: (pointerAngle * 180 / Math.PI).toFixed(1) + '°'
        });
      }

      this.drawArc();
    },
    onTouchEnd() {
      this.dragging = false;
      this.isDraggingDataArc = false;
    },

    // 检查点击位置是否在数据弧线附近
    isNearDataArc(angle) {
      const dataArcStart = this.normalizeAngle(this.dataStartAngle);
      const dataArcEnd = this.normalizeAngle(this.dataStartAngle + this.dataArcLength);
      const normalizedAngle = this.normalizeAngle(angle);

      // 计算到数据弧线的最短距离
      let distance = Math.min(
        Math.abs(normalizedAngle - dataArcStart),
        Math.abs(normalizedAngle - dataArcEnd)
      );

      // 处理跨越0度的情况
      if (dataArcStart <= dataArcEnd) {
        // 圆弧不跨越0度
        if (normalizedAngle >= dataArcStart && normalizedAngle <= dataArcEnd) {
          distance = 0; // 在圆弧内
        }
      } else {
        // 圆弧跨越0度
        if (normalizedAngle >= dataArcStart || normalizedAngle <= dataArcEnd) {
          distance = 0; // 在圆弧内
        }
      }

      // 0.3弧度（约17度）的容差
      return distance < 0.3;
    },

    // 检查点击位置是否在红色圆弧附近
    isNearRedArc(angle) {
      const redArcStart = this.normalizeAngle(this.startAngle);
      const redArcEnd = this.normalizeAngle(this.startAngle + this.arcLen);
      const normalizedAngle = this.normalizeAngle(angle);

      // 计算到红色圆弧的最短距离
      let distance = Math.min(
        Math.abs(normalizedAngle - redArcStart),
        Math.abs(normalizedAngle - redArcEnd)
      );

      // 处理跨越0度的情况
      if (redArcStart <= redArcEnd) {
        // 圆弧不跨越0度
        if (normalizedAngle >= redArcStart && normalizedAngle <= redArcEnd) {
          distance = 0; // 在圆弧内
        }
      } else {
        // 圆弧跨越0度
        if (normalizedAngle >= redArcStart || normalizedAngle <= redArcEnd) {
          distance = 0; // 在圆弧内
        }
      }

      // 0.3弧度（约17度）的容差
      return distance < 0.3;
    },

    // 鼠标事件处理（用于桌面端测试）
    getMousePos(e) {
      const rect = this.$refs.myCanvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    },

    onMouseDown(e) {
      e.preventDefault();
      const pos = this.getMousePos(e);
      const angle = this.angleFromCenter(pos);

      // 优先检查是否点击在数据弧线附近
      if (this.isNearDataArc(angle)) {
        this.isDraggingDataArc = true;
        this.dataArcDragOffset = angle - this.dataStartAngle;
        console.log('鼠标开始拖拽数据弧线');
      }
      // 检查是否点击在红色圆弧附近
      else if (this.isNearRedArc(angle)) {
        this.dragging = true;
        this.dragOffset = angle - this.startAngle;
        this.dragStartAngle = this.startAngle;
        console.log('鼠标开始拖拽红色圆弧');
      } else {
        this.dragging = false;
        this.isDraggingDataArc = false;
      }
    },

    onMouseMove(e) {
      if (!this.dragging && !this.isDraggingDataArc) return;
      e.preventDefault();
      const pos = this.getMousePos(e);
      let angle = this.angleFromCenter(pos);

      if (this.isDraggingDataArc) {
        // 处理数据弧线拖拽
        let newDataStart = angle - this.dataArcDragOffset;

        // 黑色指针的位置
        const pointerPosition = this.pointerAngle; // -Math.PI / 2

        // 计算滑动限制：
        // 1. 向右滑动限制：数据弧线起始位置不能超过指针位置
        const maxDataStart = pointerPosition;

        // 2. 向左滑动限制：数据弧线末尾位置不能超过指针位置
        // 当数据弧线末尾与指针重叠时：newDataStart + dataArcLength = pointerPosition
        // 所以：newDataStart = pointerPosition - dataArcLength
        const minDataStart = pointerPosition - this.dataArcLength;

        // 标准化角度到 [-π, π] 范围，避免跳转问题
        const normalizeToRange = (angle) => {
          while (angle > Math.PI) angle -= 2 * Math.PI;
          while (angle <= -Math.PI) angle += 2 * Math.PI;
          return angle;
        };

        newDataStart = normalizeToRange(newDataStart);
        const normalizedMin = normalizeToRange(minDataStart);
        const normalizedMax = normalizeToRange(maxDataStart);

        // 应用滑动限制，防止跳转
        if (normalizedMin <= normalizedMax) {
          // 正常情况，不跨越-π/π边界
          if (newDataStart < normalizedMin) {
            newDataStart = normalizedMin;
            console.log('达到左滑动限制：数据弧线末尾与指针重叠');
          }
          if (newDataStart > normalizedMax) {
            newDataStart = normalizedMax;
            console.log('达到右滑动限制：数据弧线起始位置与指针重叠');
          }
        } else {
          // 跨越-π/π边界的情况
          if (newDataStart > normalizedMax && newDataStart < normalizedMin) {
            // 在禁止区域内，选择最近的边界
            const distToMax = Math.abs(newDataStart - normalizedMax);
            const distToMin = Math.abs(newDataStart - normalizedMin);
            if (distToMax < distToMin) {
              newDataStart = normalizedMax;
              console.log('达到右滑动限制（跨边界）');
            } else {
              newDataStart = normalizedMin;
              console.log('达到左滑动限制（跨边界）');
            }
          }
        }

        this.dataStartAngle = newDataStart;

        // 计算当前指针位置对应的时间索引，实现数据同步
        this.updateCurrentTimeIndexFromDataArc();

        // 计算数据弧线的结束位置用于调试
        const dataEndAngle = normalizeToRange(newDataStart + this.dataArcLength);
        console.log('数据弧线起始位置:', (newDataStart * 180 / Math.PI).toFixed(1) + '°');
        console.log('数据弧线结束位置:', (dataEndAngle * 180 / Math.PI).toFixed(1) + '°');
        console.log('指针位置:', (pointerPosition * 180 / Math.PI).toFixed(1) + '°');
      } else if (this.dragging) {
        // 处理原有红色弧线拖拽
        let newStart = angle - this.dragOffset;
        this.startAngle = newStart;
        console.log('红色弧线位置:', {
          start: (newStart * 180 / Math.PI).toFixed(1) + '°',
          end: ((newStart + this.arcLen) * 180 / Math.PI).toFixed(1) + '°'
        });
      }

      this.drawArc();
    },

    onMouseUp() {
      this.dragging = false;
      this.isDraggingDataArc = false;
    },
    drawArc() {
      const canvas = this.$refs.myCanvas;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 画底色弧
      ctx.beginPath();
      ctx.arc(this.center.x, this.center.y, this.radius, this.baseStart, this.baseEnd, false);
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = this.baseArcLineWidth;
      ctx.stroke();

      // 画红色数据弧线（分割成7200段）
      this.drawDataArc(ctx);

      // 绘制时间刻度
      this.drawTimeScale(ctx);

      // 画canvas正中间的定位指针
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(this.center.x, this.center.y);
      ctx.lineTo(this.center.x, this.center.y - this.radius - this.pointerLength);
      ctx.strokeStyle = '#222';
      ctx.lineWidth = this.pointerLineWidth;
      ctx.stroke();

      // 在指针顶端添加一个三角形标记
      const triangleSize = 8 * this.scaleFactor;
      const triangleOffset = 15 * this.scaleFactor;
      ctx.beginPath();
      ctx.moveTo(this.center.x, this.center.y - this.radius - this.pointerLength);
      ctx.lineTo(this.center.x - triangleSize, this.center.y - this.radius - this.pointerLength - triangleOffset);
      ctx.lineTo(this.center.x + triangleSize, this.center.y - this.radius - this.pointerLength - triangleOffset);
      ctx.closePath();
      ctx.fillStyle = '#222';
      ctx.fill();

      // 如果指针在数据弧线范围内，在数据弧线上标记当前位置
      if (this.currentPointerValue) {
        const pointerX = this.center.x + Math.cos(this.pointerAngle) * this.radius;
        const pointerY = this.center.y + Math.sin(this.pointerAngle) * this.radius;
        const markerRadius = 8 * this.scaleFactor;

        ctx.beginPath();
        ctx.arc(pointerX, pointerY, markerRadius, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.strokeStyle = '#222';
        ctx.lineWidth = Math.max(2, 3 * this.scaleFactor);
        ctx.stroke();
      }

      ctx.restore();
    },

    // 绘制时间文本（10分钟为一个间隔，不显示刻度线）
    drawTimeScale(ctx) {
      if (!this.list || this.list.length === 0) {
        return;
      }

      ctx.save();

      // 获取数据的时间范围
      const firstTime = new Date(this.list[this.list.length - 1].receivedTime);
      const lastTime = new Date(this.list[0].receivedTime);
      const totalTimeSpan = Math.abs((lastTime - firstTime) / 1000); // 总时间跨度（秒）

      // 10分钟 = 600秒
      const scaleInterval = 600; // 10分钟间隔
      const scaleCount = Math.ceil(totalTimeSpan / scaleInterval);

      // 计算每个刻度在弧线上的角度间隔
      const anglePerScale = this.dataArcLength / (totalTimeSpan / scaleInterval);

      // 只绘制时间标签，不绘制刻度线
      for (let i = 0; i <= scaleCount; i++) {
        // 计算当前刻度的角度位置
        const scaleAngle = this.dataStartAngle + (i * anglePerScale);

        // 如果刻度超出了数据弧线范围，跳过
        if (i * anglePerScale > this.dataArcLength) {
          break;
        }

        // 计算对应的时间
        const scaleTime = new Date(firstTime.getTime() + (i * scaleInterval * 1000));
        const timeLabel = scaleTime.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

        // 绘制时间标签（横放显示）
        // 标签位置在弧线外侧
        const labelRadius = this.radius + 25 * this.scaleFactor;
        const labelX = this.center.x + Math.cos(scaleAngle) * labelRadius;
        const labelY = this.center.y + Math.sin(scaleAngle) * labelRadius;

        ctx.fillStyle = '#333';
        ctx.font = `${Math.max(10, 12 * this.scaleFactor)}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 时间文本横放显示，不跟随角度旋转
        ctx.fillText(timeLabel, labelX, labelY);
      }

      ctx.restore();
    },

    // 绘制多彩数据弧线 - 基于状态的颜色
    drawDataArc(ctx) {
      if (this.dataValues.length === 0) {
        console.log('没有数据值，跳过绘制');
        return;
      }

      console.log('开始绘制数据弧线，数据长度:', this.dataValues.length);
      console.log('segmentAngle:', this.segmentAngle);
      console.log('dataStartAngle:', this.dataStartAngle);
      console.log('dataArcLength:', this.dataArcLength);

      ctx.save();
      ctx.lineWidth = this.dataArcLineWidth; // 使用响应式线宽
      ctx.lineCap = 'butt'; // 设置线条端点样式，确保无缝连接

      // 按状态分组，将相邻的相同状态合并为连续段
      const statusSegments = [];
      let currentSegment = null;

      for (let i = 0; i < this.dataValues.length; i++) {
        const dataObj = this.dataValues[i];

        if (!currentSegment || currentSegment.colorType !== dataObj.colorType) {
          // 开始新的段
          if (currentSegment) {
            statusSegments.push(currentSegment);
          }
          currentSegment = {
            start: i,
            end: i,
            colorType: dataObj.colorType,
            status: dataObj.status,
            values: [dataObj.value]
          };
        } else {
          // 继续当前段
          currentSegment.end = i;
          currentSegment.values.push(dataObj.value);
        }
      }

      // 添加最后一个段
      if (currentSegment) {
        statusSegments.push(currentSegment);
      }

      console.log('状态段数量:', statusSegments.length);

      // 绘制每个状态段
      statusSegments.forEach((segment, index) => {
        // 计算该段的起始和结束角度
        const segmentStartAngle = this.dataStartAngle + segment.start * this.segmentAngle;
        const segmentEndAngle = this.dataStartAngle + (segment.end + 1) * this.segmentAngle;

        // 计算该段的平均数据值来确定透明度
        const avgValue = segment.values.reduce((sum, val) => sum + val, 0) / segment.values.length;
        const intensity = avgValue / 100;
        const alpha = 0.6 + intensity * 0.4; // 调整透明度范围，使其更明显

        // 根据状态设置颜色
        let strokeStyle;
        switch (segment.colorType) {
          case 'red':     // running 跑
            strokeStyle = `rgba(255, 0, 0, ${alpha})`;
            break;
          case 'yellow':  // sitting 坐
            strokeStyle = `rgba(255, 255, 0, ${alpha})`;
            break;
          case 'green':   // standing 站立
            strokeStyle = `rgba(0, 255, 0, ${alpha})`;
            break;
          case 'purple':  // walking 行走
            strokeStyle = `rgba(128, 0, 128, ${alpha})`;
            break;
          case 'blue':    // lying 爬
            strokeStyle = `rgba(0, 0, 255, ${alpha})`;
            break;
          case 'gray':    // eatting 吃
            strokeStyle = `rgba(128, 128, 128, ${alpha})`;
            break;
          default:
            strokeStyle = `rgba(255, 0, 0, ${alpha})`;
        }

        ctx.strokeStyle = strokeStyle;

        console.log(`绘制段 ${index}: ${segment.status} (${segment.colorType}), 角度: ${segmentStartAngle} - ${segmentEndAngle}`);

        // 绘制整个状态段作为一个连续的弧线
        ctx.beginPath();
        ctx.arc(this.center.x, this.center.y, this.radius, segmentStartAngle, segmentEndAngle, false);
        ctx.stroke();
      });

      ctx.restore();
    },

    // 获取状态的中文文本
    getStatusText(status) {
      const statusTextMap = {
        'running': '跑',
        'sitting': '坐',
        'standing': '站立',
        'walking': '行走',
        'lying': '爬',
        'eatting': '吃'
      };
      return statusTextMap[status] || status;
    },

    // 获取状态对应的颜色
    getStatusColor(status) {
      const statusColorMap = {
        'running': '#ff0000',    // 跑 - 红色
        'sitting': '#ffaa00',    // 坐 - 黄色
        'standing': '#00ff00',   // 站立 - 绿色
        'walking': '#800080',    // 行走 - 紫色
        'lying': '#0000ff',      // 爬 - 蓝色
        'eatting': '#808080'     // 吃 - 灰色
      };
      return statusColorMap[status] || '#ff0000';
    }
  }
}
</script>

<style scoped>
.canvasBox {
  width: 100%;
  overflow: hidden;
  height: 100px;
}
</style>